import { Img } from '@shared/Frontend/components/img'
import { NavItemLayout, NavLayout, ParentNavItem } from '@shared/Frontend/components/navItems.layout'
import { SVGIcon } from '@shared/Frontend/components/svg'
import { useEventListener } from '@shared/Frontend/hooks/useEventListener'
import { cn } from '@shared/Frontend/utils/addToClassName'
import { translate } from '@shared/Frontend/utils/translate'
import { memo, useEffect, useRef, useState } from 'react'
import { Link } from 'widgets-for-react'
import { CustomParsedNode } from '../../legacy/public_site/components/drupal/types_and_parser/extra_types.mod'
import { Language, ParsedNode } from '../../legacy/public_site/components/drupal/types_and_parser/types.ext.gen'
import { languages } from '../../legacy/public_site/components/public_site/utils/history.mod'
import React = require('react')

export const SiteheaderLayout = ({
  language,
  isDesktop,
  items,
  isMenuOpen,
  node,
  toggleMenu
}: {
  language: string
  isDesktop: boolean
  node: ParsedNode | CustomParsedNode | 'none'
  isMenuOpen: boolean
  toggleMenu: (_: boolean) => void
  items: ParentNavItem[]
}) => {
  // Close mobile hamburger menu on Escape
  const togglerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)
  const [menuScrollState, setMenuScrollState] = useState<'default' | 'sticky' | 'reset' | 'outside'>('default')
  const [menuState, setMenuState] = useState<'open' | 'closed'>('closed')
  useEventListener(
    'keydown',
    (e): void => {
      if (e.key === 'Escape') {
        toggleMenu(false)
        if (togglerRef.current) togglerRef.current.focus()
      }
    },
    { passive: true },
    !isDesktop && isMenuOpen
  )

  let scrollPosition = 0;
  let isAnimating = false;
  useEventListener(
    'scroll',
    (e): void => {
      if(isAnimating == true) return;
      
      const headerHeight = headerRef.current?.getBoundingClientRect().height || 0;
      const newScrollPosition = window.scrollY;
      const scrollDirection = (newScrollPosition > scrollPosition) ? 'down' : 'up';

      if(scrollDirection == 'up') {
        if(newScrollPosition > headerHeight) {
          setMenuScrollState('sticky')
        } else {
          setMenuScrollState('default')
        }
      } else {
        if(newScrollPosition > headerHeight) {
          if(menuScrollState == 'sticky') {
            isAnimating = true;
            setMenuScrollState('reset')
            setTimeout(() => {
              setMenuScrollState('outside')
              isAnimating = false;
            }, 400);
          } else {
            setMenuScrollState('outside')
          }
        } else {
          setMenuScrollState('default')
        }
      }

      scrollPosition = newScrollPosition;
    },
    { passive: true }
  )

  // Set data attribute on html element to indicate menu state and prevent page scrolling
  useEffect(() => {
    if (isDesktop) {
      setMenuState('closed')
      document.documentElement.removeAttribute('data-menustate')
    } else {
      const newMenuState = isMenuOpen ? 'open' : 'closed'
      setMenuState(newMenuState)
      document.documentElement.setAttribute('data-menustate', newMenuState)
    }
  }, [isMenuOpen, isDesktop])

  // Update data-menuscroll attribute when state changes
  useEffect(() => {
    document.documentElement.setAttribute('data-menuscroll', menuScrollState)
  }, [menuScrollState])

  return (
    <>
      <SkipLinks />
      <header id="a11y-siteheader" ref={headerRef} className={cn('siteheader sh', `sh--${node != 'none' ? node.bundle : 'none'}`)}>
        <div className="sh__inner ls-container ls-gutter">
          <Link to={`/${language}`} title={translate('Header:A11y:ToHome')} className="sh__logo">
            <span className="sr-text">{translate('Header:A11y:ToHome')}</span>
            <Img
              src={`/frontend/images/${isDesktop ? 'logo--white' : 'logo'}.svg`}
              width="200"
              height="64"
              alt="Logo Van Oord, Marine Ingenuity"
              loading="eager"
              fetchPriority="high"
            />
          </Link>

          {isDesktop && <MainNav items={items} />}

          <NavLayout
            navClassName="sh__secondary-nav nav--sh-secondary"
            aria-label={translate('Header:A11y:UtilityNavigation')}
          >
            {languages && languages.length > 1 && languages.every((lang) => lang.Value.toLowerCase() != 'ru') && (
              <li className="nav__item">
                <NavLayout
                  ariaLabel={translate('Header:A11y:ChooseLanguage')}
                  navClassName="sh__languages nav--languages"
                >
                  {languages.map((lang) => (
                    <li className="nav__item" key={lang.Value}>
                      <a
                        href={getLanguageSwitchUrl(node, lang.Value)}
                        aria-current={language == lang.Value ? true : undefined}
                        className="nav__link"
                      >
                        <LanguageLabel langCode={lang.Value} />
                      </a>
                    </li>
                  ))}
                </NavLayout>
              </li>
            )}

            {/* Search */}
            <li className="nav__item">
              <Link
                className="nav__link nav__link--search"
                to={`/${language}/search`}
                title={translate('Header:Search')}
              >
                <span className="sr-text">{translate('Header:Search')}</span>
                <SVGIcon id="search" />
              </Link>
            </li>
          </NavLayout>

          <div className="sh__toggler" hidden={isDesktop} ref={togglerRef}>
            <button
              className="sh__toggler-button"
              onClick={() => toggleMenu(!isMenuOpen)}
              aria-controls="a11y-nav-main"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-text">
                {isMenuOpen ? translate('Header:A11y:CloseMenu') : translate('Header:A11y:OpenMenu')}
              </span>
              <span />
              <span />
              <span />
            </button>
          </div>

          {!isDesktop && <MainNav items={items} />}
        </div>
      </header>
    </>
  )
}

const SkipLinks = () => (
  <nav aria-label={translate('Header:A11y:SkipLinks')}>
    <a href="#a11y-main" className="sr-text">
      {translate('Header:A11y:SkipToContent')}
    </a>
    <a href="#a11y-footer" className="sr-text">
      {translate('Header:A11y:SkipToFooter')}
    </a>
  </nav>
)

const MainNav = ({ items }: { items: ParentNavItem[] }) =>
  items &&
  items.length > 0 && (
    <NavLayout
      id="a11y-nav-main"
      navClassName="sh__main-nav nav--sh-primary"
      ariaLabel={translate('Header:A11y:PrimaryNavigation')}
    >
      {items.map((item, index) => (
        <NavItemLayout
          key={index}
          title={item.title || ''}
          url={item.url || ''}
          isActive={window.location.pathname.replace(/\/*$/, '') === item.url.replace(/\/*$/, '')}
          isExternal={item.isExternal || false}
          subNav={item.subNav}
        />
      ))}
    </NavLayout>
  )

const LanguageLabel = memo(({ langCode }: { langCode: string }) => (
  <abbr lang={langCode} title={getDisplayName(langCode)}>
    {langCode}
  </abbr>
))

const getDisplayName = (langCode: string) => new Intl.DisplayNames(langCode, { type: 'language' }).of(langCode)

const getLanguageSwitchUrl = (node: ParsedNode | CustomParsedNode | 'none', lang: Language): string => {
  const path = window.location.pathname
  if (path.includes('en/careers/vacancies')) return '/nl/werken-bij/vacatures'
  if (path.includes('nl/werken-bij/vacatures')) return '/en/careers/vacancies'
  if (path.includes('en/vacancy')) return path.replace('en/vacancy', 'nl/vacature')
  if (path.includes('nl/vacature')) return path.replace('nl/vacature', 'en/vacancy')
  if (path.includes('en/search')) return path.replace('en/search', 'nl/search')
  if (path.includes('nl/search')) return path.replace('nl/search', 'en/search')

  if (node !== 'none' && node.bundle != 'homepage' && node.language_links != undefined && node.language_links.has(lang))
    return node.language_links.get(lang)

  return `/${lang}`
}
